import React, { useEffect, useState } from "react";
import { Table, Button, Empty, Spin, Select, Row, Col } from "antd";
import { PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getRequest } from "../utils/apicalls";
import AdminSidebar from "../components/AdminSidebar";
import mytLogo from "../image/myt logo.d51e67ca4d4eeea6450b.png";
import "../styles/internstable.css";

const { Option } = Select;

const InternsTable = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [userData, setUserData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filterBatch, setFilterBatch] = useState(null);
  const [filterSchool, setFilterSchool] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [searchName, setSearchName] = useState("");
  const [selectedName, setSelectedName] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);

  const navigate = useNavigate();

  const fetchInterns = async () => {
    setLoading(true);
    try {
      const response = await getRequest("interns/get");

      const list = response?.data || response || [];

      if (!Array.isArray(list)) {
        setUserData([]);
        setFilteredData([]);
        return;
      }

      const transformed = list.map((intern) => ({
        id: intern.id,

        name: `${intern.first_name || ""} ${
          intern.middle_name ? intern.middle_name.charAt(0) + "." : ""
        } ${intern.last_name || ""}${
          intern.suffix ? ", " + intern.suffix : ""
        }`.trim(),

        school: intern.school || "N/A",
        company_email: intern.company_email || "N/A",
        type: intern.type || "N/A",

        batch: (intern.batch || intern.batch_number || intern.batch_id || "N/A")
          .toString()
          .trim(),
      }));

      setUserData(transformed);
      setFilteredData(transformed);
    } catch (err) {
      console.error("Fetch error:", err);
      setUserData([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInterns();

    const handleFocus = () => {
      fetchInterns();
    };

    window.addEventListener("focus", handleFocus);

    return () => window.removeEventListener("focus", handleFocus);
  }, []);

  useEffect(() => {
    let data = [...userData];

    const nameFilter = selectedName || searchName || "";

    if (nameFilter.trim()) {
      const searchTerm = nameFilter.trim().toLowerCase();
      data = data.filter((item) => item.name?.toLowerCase().includes(searchTerm));
    }

    if (filterBatch) {
      data = data.filter((item) => String(item.batch) === String(filterBatch));
    }

    if (filterSchool) {
      data = data.filter(
        (item) => item.school?.toLowerCase() === filterSchool?.toLowerCase()
      );
    }

    if (filterType) {
      data = data.filter(
        (item) => item.type?.toLowerCase() === filterType?.toLowerCase()
      );
    }

    data.sort((a, b) => {
      const aNum = Number(a.batch);
      const bNum = Number(b.batch);

      if (isNaN(aNum) && isNaN(bNum)) return 0;
      if (isNaN(aNum)) return 1;
      if (isNaN(bNum)) return -1;

      return aNum - bNum;
    });

    setFilteredData(data);
    setCurrentPage(1);
  }, [filterBatch, filterSchool, filterType, searchName, selectedName, userData]);

  const uniqueBatches = [...new Set(userData.map((i) => String(i.batch)))].sort(
    (a, b) => {
      const aNum = Number(a);
      const bNum = Number(b);

      if (isNaN(aNum) && isNaN(bNum)) return 0;
      if (isNaN(aNum)) return -1;
      if (isNaN(bNum)) return 1;

      return aNum - bNum;
    }
  );
  const uniqueSchools = [...new Set(userData.map((i) => i.school))].sort(
    (a, b) => (a || "").localeCompare(b || "", undefined, { sensitivity: "base" })
  );

  const uniqueTypes = [...new Set(userData.map((i) => i.type))];
  const uniqueNames = [...new Set(userData.map((i) => i.name))].sort((a, b) =>
    (a || "").localeCompare(b || "", undefined, { sensitivity: "base" })
  );

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      align: "center",
      width: 90,
    },
    {
      title: "Batch",
      dataIndex: "batch",
      align: "center",
      width: 100,
    },
    {
      title: "Name",
      dataIndex: "name",
      align: "center",
      ellipsis: true,
      render: (text, record) => (
        <span
          style={{ cursor: "pointer" }}
          onClick={() =>
            window.open(`/interns/${record.id}/attendance`, "_blank")
          }
        >
          {text}
        </span>
      ),
    },
    {
      title: "School",
      dataIndex: "school",
      align: "center",
      width: 220,
      ellipsis: true,
    },
    {
      title: "Company Email",
      dataIndex: "company_email",
      align: "center",
      width: 280,
      ellipsis: true,
    },
    {
      title: "Type",
      dataIndex: "type",
      align: "center",
    },

    // ✅ ADDED ACTION COLUMN
    {
      title: "Action",
      align: "center",
      render: (_, record) => (
        <Button
          type="default"
          onClick={() => navigate(`/interns/${record.id}`)}
        >
          View Profile
        </Button>
      ),
    },
  ];

  return (
    <div
      className={`list-record-container ${
        collapsed ? "collapsed" : "expanded"
      }`}
    >
      <AdminSidebar collapsed={collapsed} onCollapse={setCollapsed} />

      <div className="list-dashboard-content overflow-y-auto p-4">
        <div className="list-header-container">
          <img src={mytLogo} alt="MYT Logo" className="list-myt-logo" />
          <div className="list-header-text">
            <h2 className="list-company-name text-xl font-bold">
              MYT SoftDev Solutions, Inc.
            </h2>
            <p className="list-company-address text-sm">
              301 The Greenery, Cebu City
            </p>
          </div>
        </div>

        <h3 className="list-title-header text-lg font-semibold">
          Interns Record
        </h3>

        <Row justify="space-between" style={{ marginBottom: 15 }}>
          <Col>
            <Row gutter={10}>
              <Col>
                <Select
                  className="intern-name-search"
                  allowClear
                  showSearch
                  suffixIcon={<SearchOutlined />}
                  placeholder="Search name"
                  value={selectedName}
                  searchValue={searchName}
                  onSearch={(value) => {
                    setSearchName(value);
                    if (value) setSelectedName(null);
                  }}
                  onChange={(value) => {
                    setSelectedName(value || null);
                    setSearchName("");
                  }}
                  onClear={() => {
                    setSelectedName(null);
                    setSearchName("");
                  }}
                  optionFilterProp="label"
                  options={uniqueNames.map((name) => ({
                    label: name,
                    value: name,
                  }))}
                  style={{ width: 280 }}
                />
              </Col>

              <Col>
                <Select
                  placeholder="Batch"
                  allowClear
                  showSearch
                  style={{ width: 160 }}
                  onChange={(value) => setFilterBatch(value != null ? String(value) : null)}
                >
                  {uniqueBatches.map((b, i) => (
                    <Option key={i} value={String(b)}>
                      {b}
                    </Option>
                  ))}
                </Select>
              </Col>

              <Col>
                <Select
                  placeholder="School"
                  allowClear
                  showSearch
                  style={{ width: 180 }}
                  onChange={setFilterSchool}
                >
                  {uniqueSchools.map((s, i) => (
                    <Option key={i} value={s}>
                      {s}
                    </Option>
                  ))}
                </Select>
              </Col>

              <Col>
                <Select
                  placeholder="Type"
                  allowClear
                  style={{ width: 150 }}
                  onChange={setFilterType}
                >
                  {uniqueTypes.map((t, i) => (
                    <Option key={i} value={t}>
                      {t}
                    </Option>
                  ))}
                </Select>
              </Col>
            </Row>
          </Col>

          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate("/create-intern")}
            >
              Add Intern
            </Button>
          </Col>
        </Row>

        <Spin spinning={loading}>
          <div className="list-table-wrapper">
            <Table
              dataSource={filteredData.map((item, i) => ({
                ...item,
                key: i,
              }))}
              columns={columns}
              scroll={{ x: "max-content" }}
              tableLayout="fixed"
              locale={{
                emptyText: <Empty description="No Intern Records Yet" />,
              }}
              className="list-attendance-table"
              pagination={{
                current: currentPage,
                pageSize: 10,
                total: filteredData.length,
                onChange: setCurrentPage,
                showSizeChanger: false,
              }}
            />
          </div>
        </Spin>
      </div>
    </div>
  );
};

export default InternsTable;
